import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionService } from '../services/subscription.service';

export interface PassCheckoutController {
  requestCheckout: (passTier: string) => void;
  isDialogOpen: boolean;
  isSubmitting: boolean;
  error: string | null;
  submitEmail: (email: string) => void;
  closeDialog: () => void;
}

/**
 * Shared pass checkout behaviour for the landing cards and the pricing page.
 * A logged-in visitor goes straight to Stripe; an anonymous one is asked for an
 * email first, because the backend requires it to create a guest customer.
 */
export const usePassCheckout = (): PassCheckoutController => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [dialogTier, setDialogTier] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startCheckout = useCallback(
    async (passTier: string, email?: string) => {
      setIsSubmitting(true);
      setError(null);
      try {
        const data = await subscriptionService.createPublicPassCheckout(passTier, email);
        const url = data?.url;
        if (url && typeof url === 'string') {
          window.location.href = url;
          return;
        }
        setError(t('landing.pricing.passes.checkoutError'));
      } catch (err) {
        const message = (err as { response?: { data?: { message?: string } } })?.response
          ?.data?.message;
        setError(message || t('landing.pricing.passes.checkoutError'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [t],
  );

  const requestCheckout = useCallback(
    (passTier: string) => {
      setError(null);
      if (isAuthenticated) {
        void startCheckout(passTier);
        return;
      }
      setDialogTier(passTier);
    },
    [isAuthenticated, startCheckout],
  );

  const submitEmail = useCallback(
    (email: string) => {
      if (!dialogTier) return;
      void startCheckout(dialogTier, email);
    },
    [dialogTier, startCheckout],
  );

  const closeDialog = useCallback(() => {
    setDialogTier(null);
    setError(null);
  }, []);

  return {
    requestCheckout,
    isDialogOpen: dialogTier !== null,
    isSubmitting,
    error,
    submitEmail,
    closeDialog,
  };
};

export default usePassCheckout;
